import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, Phone, MessageCircle, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [contactSettings, setContactSettings] = useState({
    admin_phone: "+91 96991 36479",
    admin_whatsapp: "919699136479",
    admin_email: "contact@capinrealtypune.com",
    support_hours: "Mon-Sat, 9:00 AM - 6:00 PM IST"
  });

  useEffect(() => {
    // Fetch contact settings from backend
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        if (response.data) {
          setContactSettings({
            admin_phone: response.data.admin_phone || "+91 96991 36479",
            admin_whatsapp: response.data.admin_whatsapp || "919699136479",
            admin_email: response.data.admin_email || "contact@capinrealtypune.com",
            support_hours: response.data.support_hours || "Mon-Sat, 9:00 AM - 6:00 PM IST"
          });
        }
      } catch (error) {
        console.log("Could not fetch site settings, using defaults");
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowContactInfo(true);
  };

  // Format phone for display (add spaces)
  const formatPhoneDisplay = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+91')) {
      const digits = cleaned.substring(3);
      return `+91 ${digits.substring(0, 5)} ${digits.substring(5)}`;
    }
    if (cleaned.length === 10) {
      return `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
    }
    return phone;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/157811/pexels-photo-157811.jpeg')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-slate-900/80 flex items-center justify-center">
          <div className="text-white text-center px-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_4901b1f7-f613-488e-b000-d3e46b5149ad/artifacts/g01ubmeo_IMG_20250901_221756_327.webp" 
              alt="CAPIN Realty" 
              className="h-32 w-auto mx-auto mb-6"
            />
            <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              CAPIN Realty
            </h1>
            <p className="text-xl text-slate-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Professional Real Estate Management Platform
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <Button
            onClick={() => navigate("/login")}
            variant="ghost"
            className="mb-6 -ml-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Button>

          <div className="mb-10">
            <h2 className="text-4xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Forgot Password?
            </h2>
            <p className="text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              For security reasons, please contact our admin team to reset your password.
            </p>
          </div>

          {!showContactInfo ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Your Email Address
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-11 rounded-xl border-slate-200 focus:border-blue-900 focus:ring-1 focus:ring-blue-900 bg-white"
                    required
                    placeholder="Enter your registered email"
                  />
                </div>
              </div>

              <Button
                data-testid="request-reset-button"
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-[#1e3a5f] to-[#162c46] hover:from-[#162c46] hover:to-[#102a43] text-white rounded-xl font-medium shadow-lg"
              >
                Get Admin Contact Info
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Warning Notice */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1">
                      Password Reset Requires Admin Assistance
                    </p>
                    <p className="text-xs text-amber-800">
                      For security purposes, password resets are handled manually by our admin team. 
                      Please contact us using the information below and provide your registered email: <strong>{email}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Admin Card */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">Contact Admin</h3>
                
                <div className="space-y-4">
                  {/* Phone */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-[#1e3a5f]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Call Admin</p>
                      <a 
                        href={`tel:+${contactSettings.admin_whatsapp}`}
                        className="text-lg font-semibold text-[#1e3a5f] hover:text-[#d4af37] transition-colors"
                      >
                        {formatPhoneDisplay(contactSettings.admin_phone)}
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <a 
                    href={`https://wa.me/${contactSettings.admin_whatsapp}?text=Hi%2C%20I%20need%20to%20reset%20my%20password%20for%20CAPIN%20Realty.%20My%20email%20is%3A%20${encodeURIComponent(email)}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
                  >
                    <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">WhatsApp</p>
                      <p className="text-lg font-semibold text-green-700 group-hover:text-green-800">
                        Chat with Admin
                      </p>
                    </div>
                  </a>

                  {/* Email Admin */}
                  <a 
                    href={`mailto:${contactSettings.admin_email}?subject=Password%20Reset%20Request&body=Hi%2C%0A%0AI%20would%20like%20to%20reset%20my%20password.%0A%0AMy%20registered%20email%3A%20${encodeURIComponent(email)}`}
                    className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                  >
                    <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                      <p className="text-lg font-semibold text-blue-700 group-hover:text-blue-800">
                        {contactSettings.admin_email}
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Working Hours */}
              <div className="text-center p-4 bg-slate-100 rounded-xl">
                <p className="text-sm text-slate-600">
                  <strong>Admin Support Hours:</strong> {contactSettings.support_hours}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Password reset requests are usually processed within 24 hours.
                </p>
              </div>

              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="w-full h-12 rounded-xl"
              >
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
